import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/app/AppLayout";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import ClubAdminDashboard from "@/pages/ClubAdminDashboard";
import ClubCategories from "@/pages/ClubCategories";
import ClubTeams from "@/pages/ClubTeams";
import ClubStaff from "@/pages/ClubStaff";
import ClubTemplates from "@/pages/ClubTemplates";
import CoordinatorDashboard from "@/pages/CoordinatorDashboard";
import CoordinatorTeams from "@/pages/CoordinatorTeams";
import CoachDashboard from "@/pages/CoachDashboard";
import CoachJornadas from "@/pages/CoachJornadas";
import JornadaRegister from "@/pages/JornadaRegister";
import TeamDetail from "@/pages/TeamDetail";
import PlayerProfile from "@/pages/PlayerProfile";
import AttendancePage from "@/pages/AttendancePage";
import ClubCalendarPage from "@/pages/ClubCalendarPage";
import CoachPizarra from "@/pages/CoachPizarra";
import PizarraLive from "@/pages/PizarraLive";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import StatsPage from "@/pages/StatsPage";
import EvaluationsPage from "@/pages/EvaluationsPage";
import InjuriesPage from "@/pages/InjuriesPage";
import PublicPlayerPage from "@/pages/PublicPlayerPage";
import PublicGuidePage from "@/pages/PublicGuidePage";
import PlayerGuidePage from "@/pages/PlayerGuidePage";
import GuidePage from "@/pages/GuidePage";
import { LegalNoticePage, PrivacyPage, CookiesPage } from "@/pages/LegalPages";
import BillingPage, { BillingSuccessPage, BillingCancelPage } from "@/pages/BillingPage";
import ClubSettings from "@/pages/ClubSettings";
import PlayersPage from "@/pages/PlayersPage";
import FamiliasPage from "@/pages/FamiliasPage";
import FamiliaDashboard from "@/pages/FamiliaDashboard";
import FamiliaLiveMatch from "@/pages/FamiliaLiveMatch";

// Apply saved theme before first paint so login/public pages also respect it.
try {
  if (localStorage.getItem("sudeporte_theme") === "light") {
    document.documentElement.classList.add("light");
  }
} catch { /* ignore */ }

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin" || user.role === "platform_sales") return <Navigate to="/platform" replace />;
  if (user.role === "club_admin") return <Navigate to={user.club_plan === "familia" ? "/familia" : "/club"} replace />;
  if (user.role === "coordinator" || user.role === "director_deportivo") return <Navigate to="/coordinator" replace />;
  if (user.role === "coach" || user.role === "delegado") return <Navigate to="/coach" replace />;
  return <Navigate to="/login" replace />;
}

function Shell({ children }) {
  return <AppLayout>{children}</AppLayout>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster theme="dark" position="top-right" richColors offset={16} toastOptions={{ style: { pointerEvents: "auto" } }} />
          {/* On mobile, Sonner's viewport can capture clicks over the hamburger — CSS below neutralizes the viewport gutter while keeping toasts clickable. */}
          <style>{`.sonner-toaster [data-sonner-toaster]{pointer-events:none}.sonner-toaster [data-sonner-toaster] > li{pointer-events:auto}@media(max-width:768px){[data-sonner-toaster][data-y-position="top"]{top:auto!important;bottom:16px!important;left:16px!important;right:16px!important}}`}</style>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public shareable player profile — NO auth */}
            <Route path="/p/:token" element={<PublicPlayerPage />} />
            {/* Public marketing/guide page — NO auth */}
            <Route path="/guia" element={<PublicGuidePage />} />
            <Route path="/guia-player" element={<PlayerGuidePage />} />
            {/* Root: la landing pública se sirve desde GHL en sudefootball.sudeporte.com.
                En app.sudefootball.sudeporte.com el root redirige al login para evitar
                duplicar la landing comercial. */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/familias" element={<FamiliasPage />} />
            <Route path="/player" element={<FamiliasPage />} />
            {/* Legal — NO auth */}
            <Route path="/aviso-legal" element={<LegalNoticePage />} />
            <Route path="/privacidad" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            {/* Authenticated home: routes to role-specific dashboard */}
            <Route path="/home" element={<ProtectedRoute><RoleHome /></ProtectedRoute>} />

            {/* Super admin */}
            <Route path="/platform" element={<ProtectedRoute roles={["super_admin", "platform_sales"]}><Shell><SuperAdmin /></Shell></ProtectedRoute>} />
            <Route path="/platform/clubs" element={<ProtectedRoute roles={["super_admin", "platform_sales"]}><Shell><SuperAdmin /></Shell></ProtectedRoute>} />

            {/* Club admin */}
            <Route path="/familia" element={<ProtectedRoute roles={["club_admin"]}><Shell><FamiliaDashboard /></Shell></ProtectedRoute>} />
            <Route path="/familia/directo" element={<ProtectedRoute roles={["club_admin"]}><Shell><FamiliaLiveMatch /></Shell></ProtectedRoute>} />
            <Route path="/club" element={<ProtectedRoute roles={["club_admin"]}><Shell><ClubAdminDashboard /></Shell></ProtectedRoute>} />
            <Route path="/club/categories" element={<ProtectedRoute roles={["club_admin"]}><Shell><ClubCategories /></Shell></ProtectedRoute>} />
            <Route path="/club/teams" element={<ProtectedRoute roles={["club_admin"]}><Shell><ClubTeams /></Shell></ProtectedRoute>} />
            <Route path="/club/staff" element={<ProtectedRoute roles={["club_admin"]}><Shell><ClubStaff /></Shell></ProtectedRoute>} />
            <Route path="/club/templates" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo"]}><Shell><ClubTemplates /></Shell></ProtectedRoute>} />
            <Route path="/club/players" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo", "coach", "delegado"]}><Shell><PlayersPage /></Shell></ProtectedRoute>} />

            {/* Coordinator */}
            <Route path="/coordinator" element={<ProtectedRoute roles={["coordinator", "director_deportivo", "club_admin"]}><Shell><CoordinatorDashboard /></Shell></ProtectedRoute>} />
            <Route path="/coordinator/teams" element={<ProtectedRoute roles={["coordinator", "director_deportivo", "club_admin"]}><Shell><CoordinatorTeams /></Shell></ProtectedRoute>} />

            {/* Coach */}
            <Route path="/coach" element={<ProtectedRoute roles={["coach", "delegado"]}><Shell><CoachDashboard /></Shell></ProtectedRoute>} />
            <Route path="/coach/jornadas" element={<ProtectedRoute roles={["coach", "delegado", "club_admin", "coordinator", "director_deportivo"]}><Shell><CoachJornadas /></Shell></ProtectedRoute>} />
            <Route path="/coach/jornadas/new" element={<ProtectedRoute roles={["coach", "delegado", "club_admin", "coordinator", "director_deportivo"]}><Shell><JornadaRegister /></Shell></ProtectedRoute>} />
            <Route path="/coach/attendance" element={<ProtectedRoute roles={["coach", "delegado", "club_admin", "coordinator", "director_deportivo"]}><Shell><AttendancePage /></Shell></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute roles={["coach", "delegado", "club_admin", "coordinator", "director_deportivo"]}><Shell><ClubCalendarPage /></Shell></ProtectedRoute>} />
            <Route path="/coach/pizarra" element={<ProtectedRoute roles={["coach", "delegado", "club_admin", "coordinator", "director_deportivo"]}><Shell><CoachPizarra /></Shell></ProtectedRoute>} />
            <Route path="/live/:userId" element={<ProtectedRoute roles={["coordinator", "club_admin", "director_deportivo", "super_admin"]}><Shell><PizarraLive /></Shell></ProtectedRoute>} />
            <Route path="/coach/live" element={<ProtectedRoute roles={["coach", "delegado", "club_admin", "coordinator", "director_deportivo"]}><Shell><Navigate to="/coach/jornadas/new" replace /></Shell></ProtectedRoute>} />

            {/* Announcements — accessible to any authenticated club user */}
            <Route path="/comms" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo", "coach", "delegado"]}><Shell><AnnouncementsPage /></Shell></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo", "coach", "delegado"]}><Shell><GuidePage /></Shell></ProtectedRoute>} />

            {/* Evaluations — assessment page, accessible to any club role */}
            <Route path="/evaluations" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo", "coach", "delegado"]}><Shell><EvaluationsPage /></Shell></ProtectedRoute>} />
            <Route path="/injuries" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo", "coach", "delegado"]}><Shell><InjuriesPage /></Shell></ProtectedRoute>} />

            {/* Audit log — visible to admin & coordinator */}
            <Route path="/club/audit" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo"]}><Shell><AuditLogPage /></Shell></ProtectedRoute>} />

            {/* Stats — accessible to all authenticated club roles */}
            <Route path="/stats" element={<ProtectedRoute roles={["club_admin", "coordinator", "director_deportivo", "coach", "delegado"]}><Shell><StatsPage /></Shell></ProtectedRoute>} />

            {/* Templates — admin-level roles */}
            <Route path="/club/templates2" element={null} />

            {/* Billing — club_admin only */}
            <Route path="/billing" element={<ProtectedRoute roles={["club_admin", "super_admin"]}><Shell><BillingPage /></Shell></ProtectedRoute>} />
            <Route path="/billing/success" element={<ProtectedRoute roles={["club_admin", "super_admin"]}><Shell><BillingSuccessPage /></Shell></ProtectedRoute>} />
            <Route path="/billing/cancel" element={<ProtectedRoute roles={["club_admin", "super_admin"]}><Shell><BillingCancelPage /></Shell></ProtectedRoute>} />
            <Route path="/club/settings" element={<ProtectedRoute roles={["club_admin", "super_admin"]}><Shell><ClubSettings /></Shell></ProtectedRoute>} />

            {/* Shared */}
            <Route path="/team/:teamId" element={<ProtectedRoute><Shell><TeamDetail /></Shell></ProtectedRoute>} />
            <Route path="/player/:playerId" element={<ProtectedRoute><Shell><PlayerProfile /></Shell></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
